import apiClient from './apiClient';
import type {
  SmtpConfigurationDto,
  UpdateSmtpConfigRequest,
  TestEmailRequest,
  TestEmailResponse,
  AlertThresholdsResponse,
  UpdateAlertThresholdsRequest,
  QueueThresholdsResponse,
  UpdateQueueThresholdsRequest,
  AlertRecipientDto,
  CreateAlertRecipientRequest,
  UpdateAlertRecipientRequest,
  AlertHistoryResponse,
  AlertHistoryDto,
  AlertHistoryFilters,
  ResolveAlertRequest
} from '../types';

/**
 * Service para gerenciar configuracoes de alertas
 */
export const alertsService = {
  // ============================================================================
  // SMTP Configuration
  // ============================================================================

  /**
   * Obtem configuracao SMTP atual
   */
  async getSmtpConfig(): Promise<SmtpConfigurationDto> {
    const response = await apiClient.get<SmtpConfigurationDto>('/alerts/smtp');
    return response.data;
  },

  /**
   * Atualiza configuracao SMTP
   */
  async updateSmtpConfig(request: UpdateSmtpConfigRequest): Promise<SmtpConfigurationDto> {
    const response = await apiClient.put<SmtpConfigurationDto>('/alerts/smtp', request);
    return response.data;
  },

  /**
   * Envia email de teste
   */
  async testSmtpConfig(request: TestEmailRequest): Promise<TestEmailResponse> {
    const response = await apiClient.post<TestEmailResponse>('/alerts/smtp/test', request);
    return response.data;
  },

  // ============================================================================
  // Alert Thresholds
  // ============================================================================

  /**
   * Obtem todos os thresholds de alerta
   */
  async getThresholds(): Promise<AlertThresholdsResponse> {
    const response = await apiClient.get<AlertThresholdsResponse>('/alerts/thresholds');
    return response.data;
  },

  /**
   * Atualiza thresholds de alerta
   */
  async updateThresholds(request: UpdateAlertThresholdsRequest): Promise<AlertThresholdsResponse> {
    const response = await apiClient.put<AlertThresholdsResponse>('/alerts/thresholds', request);
    return response.data;
  },

  /**
   * Inicializa thresholds padrao
   */
  async initializeThresholds(): Promise<void> {
    await apiClient.post('/alerts/thresholds/initialize');
  },

  // ============================================================================
  // Queue Thresholds (Service Bus Metrics)
  // ============================================================================

  /**
   * Obtem todos os thresholds de fila do Service Bus
   */
  async getQueueThresholds(): Promise<QueueThresholdsResponse> {
    const response = await apiClient.get<QueueThresholdsResponse>('/alerts/queue-thresholds');
    return response.data;
  },

  /**
   * Atualiza thresholds de fila
   */
  async updateQueueThresholds(request: UpdateQueueThresholdsRequest): Promise<QueueThresholdsResponse> {
    const response = await apiClient.put<QueueThresholdsResponse>('/alerts/queue-thresholds', request);
    return response.data;
  },

  /**
   * Inicializa thresholds de fila padrao
   */
  async initializeQueueThresholds(): Promise<void> {
    await apiClient.post('/alerts/queue-thresholds/initialize');
  },

  // ============================================================================
  // Alert Recipients
  // ============================================================================

  /**
   * Obtem todos os destinatarios de alerta
   */
  async getRecipients(): Promise<AlertRecipientDto[]> {
    const response = await apiClient.get<AlertRecipientDto[]>('/alerts/recipients');
    return response.data;
  },

  /**
   * Cria novo destinatario de alerta
   */
  async createRecipient(request: CreateAlertRecipientRequest): Promise<AlertRecipientDto> {
    const response = await apiClient.post<AlertRecipientDto>('/alerts/recipients', request);
    return response.data;
  },

  /**
   * Atualiza destinatario de alerta
   */
  async updateRecipient(email: string, request: UpdateAlertRecipientRequest): Promise<AlertRecipientDto> {
    const response = await apiClient.put<AlertRecipientDto>(
      `/alerts/recipients/${encodeURIComponent(email)}`,
      request
    );
    return response.data;
  },

  /**
   * Remove destinatario de alerta
   */
  async deleteRecipient(email: string): Promise<void> {
    await apiClient.delete(`/alerts/recipients/${encodeURIComponent(email)}`);
  },

  /**
   * Alterna status ativo/inativo do destinatario
   */
  async toggleRecipientStatus(email: string): Promise<AlertRecipientDto> {
    const response = await apiClient.patch<AlertRecipientDto>(
      `/alerts/recipients/${encodeURIComponent(email)}/toggle`
    );
    return response.data;
  },

  // ============================================================================
  // Alert History
  // ============================================================================

  /**
   * Obtem historico de alertas com filtros
   */
  async getAlertHistory(
    filters?: AlertHistoryFilters,
    beforeTimestamp?: string,
    limit: number = 50
  ): Promise<AlertHistoryResponse> {
    const params = new URLSearchParams();

    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.entityType) params.append('entityType', filters.entityType);
    if (filters?.operationType) params.append('operationType', filters.operationType);
    if (filters?.status) params.append('status', filters.status);
    if (beforeTimestamp) params.append('beforeTimestamp', beforeTimestamp);
    params.append('limit', limit.toString());

    const response = await apiClient.get<AlertHistoryResponse>(
      `/alerts/history?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Obtem contagem de alertas ativos
   */
  async getActiveAlertCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/alerts/history/active/count');
    return response.data.count;
  },

  /**
   * Marca alerta como resolvido
   */
  async resolveAlert(partitionKey: string, id: string, request?: ResolveAlertRequest): Promise<AlertHistoryDto> {
    const response = await apiClient.put<AlertHistoryDto>(
      `/alerts/history/${encodeURIComponent(partitionKey)}/${encodeURIComponent(id)}/resolve`,
      request || {}
    );
    return response.data;
  },

  /**
   * Marca todos os alertas ativos como resolvidos.
   * Retorna a contagem dos resolvidos com sucesso e dos pulados (ETag mismatch ou outras falhas pontuais).
   */
  async resolveAllAlerts(request?: ResolveAlertRequest): Promise<{ count: number; skippedCount: number; message: string }> {
    const response = await apiClient.post<{ message: string; count: number; skippedCount: number }>(
      '/alerts/history/resolve-all',
      request || {}
    );
    return {
      count: response.data.count,
      skippedCount: response.data.skippedCount ?? 0,
      message: response.data.message
    };
  }
};
